import {useLazyQuery} from "@apollo/client/react";
import {
    CtrCaracteristiques,
    CtrCaracTypes,
    CtrInscriptions,
    CtrPhotos,
    Maybe,
    Query,
    QueryCtrInscriptionsArgs,
    QueryCtrSomeCaracteristiquesArgs
} from "../graphql/generated/graphql-main.tsx";
import {gqlTemplates} from "../utils/gql-client.ts";
import {coreActions} from "../store/slice-core";
import {useXDispatch, useXSelector} from "../store/hooks.ts";
import {useEffect, useRef} from "react";
import {findBy, sortedByMany} from "../utils/array-utils.ts";
import {useWpContext} from "../components/context/WordPressProvider.tsx";
import {strEmpty} from "../utils/utils.ts";
import {cfg} from "../config.ts";

const gk1: keyof Query = "ctrInscriptions";
const gk2: keyof Query = "ctrThumbnails";
const gk3: keyof Query = "ctrSomeCaracteristiques";

/**
 * Build the ID list used to scope secondary listing queries after the site filter runs.
 * Keep this as a single pass over already-loaded listings so payload reduction does not move work to render.
 */
const listingMlsIds = (listings: CtrInscriptions[]): string[] => {
    const ids: string[] = [];
    for (const listing of listings) {
        if (listing.NO_INSCRIPTION != null)
            ids.push(listing.NO_INSCRIPTION);
    }
    return ids;
}

export default function useCtrGridListings<
    T extends CtrInscriptions,
    U extends CtrPhotos,
    V extends CtrCaracteristiques
>() {
    const dispatch = useXDispatch();
    const {others} = useWpContext<ZListings>();

    const $inscriptions = useXSelector(s => s.core.INSCRIPTIONS as T[]);
    const $thumbnails = useXSelector(s => s.core.thumbnails as U[]);
    const $some_chars = useXSelector(s => s.core.some_chars as V[]);
    const attemptedRelatedData = useRef(false);


    // differentiate sales from rentals based on presence/absence of 'PRIX_DEMANDE'

    const $sales = $inscriptions.filter(it => it.PRIX_DEMANDE != null && !strEmpty(it.PRIX_DEMANDE));
    const $rentals = $inscriptions.filter(it => it.PRIX_DEMANDE == null || strEmpty(it.PRIX_DEMANDE));


    // different sorting based on whether pagination is on or off

    const $sortedSales = sortedByMany($sales, others.paginate
        ? [{key: "DATE_MODIF", asc: false, asNumeric: false}]
        : [{key: "PRIX_DEMANDE", asc: false, asNumeric: true},
            {key: "DATE_MODIF", asc: false, asNumeric: false}]);

    const $sortedRentals = sortedByMany($rentals, others.paginate
        ? [{key: "DATE_MODIF", asc: false, asNumeric: false}]
        : [{key: "PRIX_LOCATION_DEMANDE", asc: false, asNumeric: true},
            {key: "DATE_MODIF", asc: false, asNumeric: false}]);


    // GraphQL queries for listings & related grid data

    // query all listings (only core details to be displayed on grid pages)
    const [fetchInscriptions, {loading: loading1}] = useLazyQuery<Query, QueryCtrInscriptionsArgs>(
        gqlTemplates.inscriptions, {fetchPolicy: "cache-and-network"});
    // fetch thumbnail rows only for the currently applicable listings
    const [fetchThumbnails, {loading: loading2}] = useLazyQuery<Query>(
        gqlTemplates.thumbnails, {fetchPolicy: "cache-and-network"});
    // fetch 'parking'-related characteristics only for the currently applicable listings
    const [fetchSomeChars, {loading: loading3}] = useLazyQuery<Query, QueryCtrSomeCaracteristiquesArgs>(
        gqlTemplates.ctrParkingDetails, {fetchPolicy: "cache-and-network"});


    // Functions to load site-filtered listings and their scoped related rows

    const loadListings = async () => {
        const {data} = await fetchInscriptions({variables: {broker: others.broker_id}});
        if (data?.[gk1] == null) return;
        const listings = data?.[gk1] as T[];
        dispatch(coreActions.$setInscriptions(listings));
        await loadRelatedListingsData(listings);
    }
    const loadThumbnails = async (mlsIds: string[]) => {
        const {data} = await fetchThumbnails({variables: {mlsIds}});
        if (data?.[gk2] == null) return;
        dispatch(coreActions.$setThumbnails(data?.[gk2] as U[]));
    }
    const loadSomeChars = async (mlsIds: string[]) => {
        const {data} = await fetchSomeChars({variables: {types: [CtrCaracTypes.Stat], mlsIds}});
        if (data?.[gk3] == null) return;
        dispatch(coreActions.$setSomeChars(data?.[gk3] as V[]));
    }
    /**
     * Load dependent grid data only after the listings query has established the allowed MLS IDs.
     * The ref prevents duplicate network calls when multiple components reuse this hook during initial render.
     */
    const loadRelatedListingsData = async (listings: T[]) => {
        if (attemptedRelatedData.current || listings.length == 0) return;
        const mlsIds = listingMlsIds(listings);
        if (mlsIds.length == 0) return;

        attemptedRelatedData.current = true;
        await Promise.all([
            loadThumbnails(mlsIds),
            loadSomeChars(mlsIds)
        ]);
    }


    // Given MLS ID, find its thumbnail (Centris URL)
    const centrisPhoto = (mls?: Maybe<string>): Maybe<string> => {
        return findBy($thumbnails, "NO_INSCRIPTION", mls)?.PhotoURL ?? null;
    }
    // Given MLS ID, find its thumbnail (locally downloaded URL - webp | jpeg)
    const photoByMls = (mls?: Maybe<string>): Maybe<string> => {
        const _thumbnail = findBy($thumbnails, "NO_INSCRIPTION", mls);
        const _url = `${cfg.protocol}://${cfg.media.root}/${cfg.media.path_listings}`;
        return `${_url}/${mls}/${_thumbnail?.SEQ?.padStart(2, '0')}.webp`;
    }
    const charByMls = (mls?: Maybe<string>, subType?: Maybe<string>): Maybe<V> => {
        return $some_chars.find(it => it.NO_INSCRIPTION == mls
            && it.SCARAC_CODE == subType) ?? null;
    }

    useEffect(() => {
        if ($inscriptions.length == 0) loadListings();
        else if ($thumbnails.length == 0 || $some_chars.length == 0) loadRelatedListingsData($inscriptions);
    }, [$inscriptions, $thumbnails, $some_chars]);

    return {
        $sales: $sortedSales,
        $rentals: $sortedRentals,
        $listings: [...$sortedSales, ...$sortedRentals],
        $thumbnails: $thumbnails,
        $some_chars: $some_chars,
        loading: loading1 || loading2 || loading3,
        centrisPhoto,
        photoByMls,
        charByMls
    }
}
